<script lang="ts" setup>
const appUserDataStore = useAppUserDataStore();
const {
  hasClassroomMode,
  hasLMSConfig,
  hasInitializedUserLMSConfig,
  showOtherUsersClassrooms,
  appUser,
  homePageVerticalLayout,
} = storeToRefs(appUserDataStore);

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";

import { Skeleton } from "@/components/ui/skeleton";

const showClassroomSideBar = computed(() => {
  if (hasInitializedUserLMSConfig.value == false) return false;

  return hasClassroomMode.value == true || hasLMSConfig.value == true;
});

const userClassroomDataStore = useUserClassroomsDataStore();
const {
  hasOtherUserClassrooms,
  activeAssignments,
  otherUserClassrooms,
  hasInitialClassroomDataLoad,
} = storeToRefs(userClassroomDataStore);

const onUpdateShowOtherUsersClassrooms = async () => {
  if (appUser.value == undefined) return;

  const currentValue = appUser.value.showOtherUsersClassrooms ?? true;

  appUser.value.showOtherUsersClassrooms = !currentValue;

  await appUserDataStore.saveUser();
};

const verticalLayout = ref<{ top: number; bottom: number }>({
  top: homePageVerticalLayout.value,
  bottom: 100 - homePageVerticalLayout.value,
});

const onVerticalLayoutChanged = useDebounceFn((event: any) => {
  // if (event[0] == 0 || event[0] == 100) {
  //   return;
  // }

  if (appUser.value?.hideAssignmentProgress == true) {
    return;
  }

  appUserDataStore.updateHomePageVerticalLayout(event[0]);
}, 300);

const filteredAssignments = computed(() => {
  if (showOtherUsersClassrooms.value == true) {
    return activeAssignments.value;
  }

  var otherUserClassroomIds = otherUserClassrooms.value.map(
    (classroom) => classroom.id
  );

  return activeAssignments.value.filter((assignment) => {
    return otherUserClassroomIds.includes(assignment.classroomId) != true;
  });
});

onMounted(() => {
  setupVerticalLayout();
});

watch(filteredAssignments, () => {
  setupVerticalLayout();
});

const setupVerticalLayout = () => {
  var verticalLayoutValue = homePageVerticalLayout.value ?? 50;

  if (verticalLayoutValue == 0 || verticalLayoutValue == 100) {
    verticalLayoutValue = 50;
  }

  verticalLayout.value = {
    top: verticalLayoutValue,
    bottom: 100 - verticalLayoutValue,
  };
};

const onUpdateHideAssignmentProgress = () => {
  if (appUser.value == undefined) return;

  appUser.value.hideAssignmentProgress = !appUser.value.hideAssignmentProgress;

  if (appUser.value.hideAssignmentProgress != true) {
    verticalLayout.value = {
      top: homePageVerticalLayout.value ?? 50,
      bottom: 100 - (homePageVerticalLayout.value ?? 50),
    };

    appUserDataStore.updateHomePageVerticalLayout(verticalLayout.value.top);
  }

  appUserDataStore.saveUser();
};

const showAssignmentProgress = computed(() => {
  if (appUser.value == undefined) return false;

  if (appUser.value.hideAssignmentProgress == true) return false;

  return filteredAssignments.value.length > 0;
});
</script>

<template>
  <div v-auto-animate class="flex flex-col h-full pb-2 w-full">
    <div
      v-if="hasInitialClassroomDataLoad == false"
      class="flex flex-col h-full w-full p-1"
    >
      <div
        class="flex flex-row items-end pb-2 gap-2 mb-2 border-b w-full px-2 h-[60px] pt-4"
      >
        <BaseSkeleton class="h-[26px] w-[110px]" />
      </div>
      <div
        v-for="i in 15"
        class="flex flex-row items-start w-full px-2 mb-2 border-b last:border-none pb-2"
      >
        <BaseSkeleton class="h-[25px] w-[25px] mr-2" />
        <div class="grow flex flex-col gap-2">
          <BaseSkeleton class="h-[20px] w-full" />
          <BaseSkeleton class="h-[18px] w-[110px]" />
        </div>
      </div>
    </div>

    <ResizablePanelGroup
      class="h-full min-h-0 w-full"
      direction="vertical"
      id="documents-classrooms-sidebar-group"
      @layout="onVerticalLayoutChanged"
      v-if="
        hasInitialClassroomDataLoad &&
        (filteredAssignments.length == 0 || verticalLayout.top > 0)
      "
      :key="verticalLayout.top"
    >
      <ResizablePanel
        v-if="showClassroomSideBar"
        class="flex flex-col items-center w-full px-2"
        data-step-id="classroom-sidebar"
        :default-size="verticalLayout.top"
        id="classrooms-panel"
      >
        <div
          class="flex flex-row items-center w-full justify-between border-b pt-4 pb-2 pl-2"
        >
          <label class="text-h5 font-bold"> Classrooms </label>
          <BasePopover>
            <BaseIconButton :size="35" data-step-id="classroom-popover-button">
              <icon name="material-symbols:more-vert" size="35" />
            </BaseIconButton>
            <template #content>
              <div class="flex flex-col z-50 p-1 rounded">
                <NuxtLink
                  to="/configure/lms/classrooms"
                  data-step-id="configure-classrooms-button"
                >
                  <BasePopoverMenuItem>
                    <template #icon>
                      <BaseIconTools />
                    </template>
                    <span class="ml-1"> Manage Classrooms </span>
                  </BasePopoverMenuItem>
                </NuxtLink>
                <BasePopoverMenuItem
                  v-if="appUser"
                  @click="onUpdateHideAssignmentProgress"
                >
                  <template #icon>
                    <BaseCheckbox
                      :model-value="appUser.hideAssignmentProgress"
                      class="pointer-events-none"
                      size="medium"
                    />
                  </template>
                  <span class="ml-1"> Hide Assignment Progress </span>
                </BasePopoverMenuItem>
                <BasePopoverMenuItem
                  v-if="hasOtherUserClassrooms"
                  @click="onUpdateShowOtherUsersClassrooms"
                >
                  <template #icon>
                    <BaseCheckbox
                      :model-value="showOtherUsersClassrooms"
                      class="pointer-events-none"
                      size="medium"
                    />
                  </template>
                  <span class="ml-1"> Show other teachers' classrooms? </span>
                </BasePopoverMenuItem>
              </div>
            </template>
          </BasePopover>
        </div>
        <div class="flex flex-col grow min-h-0 overflow-auto w-full pt-2">
          <ClassroomFoldersList v-if="hasLMSConfig" />
          <div v-if="hasLMSConfig != true">
            <p class="my-4 text-center">
              Would you like to set up your classrooms?
            </p>
            <NuxtLink to="/configure/lms/choose">
              <BaseOutlinedButton color="secondary">
                Set Up Classrooms
              </BaseOutlinedButton>
            </NuxtLink>
          </div>
        </div>
      </ResizablePanel>
      <div
        v-if="showAssignmentProgress"
        class="relative !h-[6px] w-full bg-background border-t border-b flex flex-row items-center justify-center"
      >
        <ResizableHandle
          id="documents-classrooms-sidebar-handle"
          class="bg-transparent"
        />
        <div class="absolute w-full flex flex-row items-center justify-center">
          <div class="flex flex-col items-center justify-center">
            <BaseIconGripHorizontal size="12" />
          </div>
        </div>
      </div>
      <ResizablePanel
        v-if="showAssignmentProgress"
        id="summary-panel"
        :default-size="verticalLayout.bottom"
      >
        <DocumentsSummary
          :filteredAssignments="filteredAssignments"
          class="overflow-auto"
        />
      </ResizablePanel>
    </ResizablePanelGroup>
  </div>
</template>

<style scoped></style>
